<script lang="ts" setup>
import { Plus, Search } from "@element-plus/icons-vue";
import { ElMessage } from "element-plus";
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  DueDateBucket,
  SupportAssignmentGroup,
  SupportDevelopmentStatus,
  SupportSummaryStatus,
  SupportTicketType,
  SysOrgRoleName,
  TicketScope,
  type SearchTicketsRequestDto,
  type SeverityOptionDto,
  type SeverityOptionsDto,
  type TicketDto,
  type TicketSystemsMetadataDto,
  type WorkflowContentDto,
  type WorkflowDto,
} from "~/api";
import WorkflowResponseViewer from "~/Compliance/components/WorkflowResponseViewer.vue";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { trackActivity } from "~/utils/activityTracker";
import { apiClient } from "~/utils/apiClient";
import { showError } from "~/utils/handleAPIError";

const myInfoStore = useMyInfoStore();
const route = useRoute();
const router = useRouter();

type OwnershipScope = "Mine" | "MyOrg" | "All";
type OwnershipInclude = "Created" | "Assigned" | "CreatedOrAssigned";
type SearchMode = "all" | "filtered" | null;

/** Support role sees extra columns/filters and can cross-search all systems.
 */
const isSupportUser = computed(
  () =>
    myInfoStore.currentSysOrg.roles.includes(SysOrgRoleName.CLIENT_SUPPORT) ||
    myInfoStore.currentSysOrg.roles.includes(SysOrgRoleName.HAVN_SUPPORT),
);

const loading = ref(false);
const tickets = ref<TicketDto[]>([]);
const total = ref(0);
const hasSearched = ref(false);
const lastSearchMode = ref<SearchMode>(null);
const severityOptions = ref<SeverityOptionsDto[]>([]);

/**
 * Which system this server is + which sibling systems this user may search, from the server.
 * isHavnSupport comes from here rather than from client-side role checks: cross-system search
 * is HAVN Support only (the backend clamps it anyway), and the server's flag also covers
 * SuperAdmin, which the role list alone would miss.
 */
const ticketSystems = ref<TicketSystemsMetadataDto | null>(null);
const isHavnSupport = computed(
  () => ticketSystems.value?.isHavnSupport ?? false,
);
const hasRemoteSystems = computed(
  () => (ticketSystems.value?.remoteSystems.length ?? 0) > 0,
);

/** Remote systems that could not be searched (down / unreachable) — shown, not swallowed. */
const searchWarnings = ref<string[]>([]);
const selectedTicket = ref<TicketDto | null>(null);
const detailLoading = ref(false);
const createTicketLoading = ref(false);

/**
 * The ticket form is a workflow, not a bespoke modal — the drawer hosts
 * WorkflowResponseViewer. The workflow's fields write back onto the ticket row via
 * SaveToTable, so opening a ticket means opening its workflow.
 */
const selectedWorkflow = ref<WorkflowDto | null>(null);
const workflowDrawerVisible = computed({
  get: () => selectedWorkflow.value !== null,
  set: (visible: boolean) => {
    if (!visible) closeWorkflowDrawer();
  },
});

const pagination = reactive({
  page: 1,
  pageSize: 25,
});

// Goes to the server — matches Subject, Created date, Primary Contact,
// Ticket #, or GitHub #.
const searchQuery = ref("");

/** Which side of "x days" a day-count filter applies to. */
type DaysComparator = "lt" | "gt";

const filters = reactive({
  scope: "Mine" as OwnershipScope,
  ownershipInclude: "CreatedOrAssigned" as OwnershipInclude,
  type: "" as SupportTicketType | "",
  severity: null as number | null,
  // "" = no due-date narrowing (server gets All) — same cleared-placeholder idiom as the
  // Type/Severity/Status selects, so all four filters read consistently. Deliberately not
  // defaulted to "On track": that excludes closed tickets AND any ticket with no SLA bucket
  // (no type/severity yet, or no band configured for its type), so it would present an
  // empty list that reads as broken filters.
  dueFilter: "" as DueDateBucket | "",
  summaryStatus: "" as SupportSummaryStatus | "",
  devStatus: "" as SupportDevelopmentStatus | "",
  // Spec: "Days Open or Last Activity — < x days / > x days". Each is a comparator plus a
  // day count; a null count means the filter is off.
  daysOpenComparator: "gt" as DaysComparator,
  daysOpen: null as number | null,
  activityComparator: "gt" as DaysComparator,
  daysSinceActivity: null as number | null,
  // Which system(s) to search: "" = current system (default), "All" = every system, or a
  // remote system's key from the systems metadata endpoint. HAVN Support only.
  system: "" as string,
});

const typeOptions: { label: string; value: SupportTicketType }[] = [
  { label: "Bug", value: SupportTicketType.BUG },
  { label: "Feature Request", value: SupportTicketType.FEATURE_REQUEST },
  { label: "System Outage", value: SupportTicketType.SYSTEM_OUTAGE },
];
const devStatusOptions = Object.values(SupportDevelopmentStatus).map(
  (value) => ({
    value,
    label: formatEnumLabel(value),
  }),
);

// The wire values stay Dev/Hotfix/Preview/Live (what the server stores and stamps); only the
// labels use the spec's environment names.
const siteTypeLabels: Record<string, string> = {
  Dev: "Test",
  Hotfix: "Hotfix",
  Preview: "UAT / Staging",
  Live: "Production",
};
const displaySiteType = (siteType?: string | null) =>
  siteType ? (siteTypeLabels[siteType] ?? siteType) : "";

/**
 * Options for the System filter, from the server's systems metadata — nothing hardcoded.
 * "" = current system, "All" = everything, else a remote system's config key.
 *
 * Remote systems are labelled by their config KEY ("QA", "UAT"...), not their site type —
 * keys are unique and named by whoever configured them, while several environments can share
 * a site type (QA and UAT can both be Preview deployments).
 */
const systemFilterOptions = computed<{ label: string; value: string }[]>(() => {
  if (!ticketSystems.value) return [];
  return [
    {
      label: `Current (${displaySiteType(ticketSystems.value.currentSiteType)})`,
      value: "",
    },
    { label: "All systems", value: "All" },
    ...ticketSystems.value.remoteSystems.map((s) => ({
      label: s.key,
      value: s.key,
    })),
  ];
});

const ownershipIncludeOptions = computed<
  {
    label: string;
    value: OwnershipInclude;
  }[]
>(() => {
  switch (filters.scope) {
    case "Mine":
      return [
        { value: "Created", label: "Created by me" },
        { value: "CreatedOrAssigned", label: "Created or assigned to me" },
      ];
    case "MyOrg":
      return [
        {
          value: "CreatedOrAssigned",
          label: "My Org tickets",
        },
      ];
    case "All":
    default:
      return [
        {
          value: "CreatedOrAssigned",
          label: "All tickets",
        },
      ];
  }
});

/**
 * Presentation for the server-computed due bucket. The bucket itself is never derived here:
 * it comes from the ticket's real SLA (per-severity, business-calendar aware), so a row can
 * never contradict the Due Date filter that selected it.
 */
const dueBucketTagType: Record<
  DueDateBucket,
  "success" | "warning" | "danger" | "info"
> = {
  [DueDateBucket.ALL]: "info",
  [DueDateBucket.OPEN]: "success",
  [DueDateBucket.DUE_SOON]: "warning",
  [DueDateBucket.OVERDUE]: "danger",
};

const dueBucketLabel: Record<DueDateBucket, string> = {
  [DueDateBucket.ALL]: "All",
  [DueDateBucket.OPEN]: "On track",
  [DueDateBucket.DUE_SOON]: "Due soon",
  [DueDateBucket.OVERDUE]: "Overdue",
};

/** Summary status is what every user sees; support additionally gets the dev status. */
const summaryStatusTagType: Record<
  SupportSummaryStatus,
  "info" | "primary" | "warning" | "success" | "danger"
> = {
  [SupportSummaryStatus.DRAFT]: "info",
  [SupportSummaryStatus.NEW]: "primary",
  [SupportSummaryStatus.STARTED]: "warning",
  [SupportSummaryStatus.COMPLETE]: "success",
  [SupportSummaryStatus.CANCELLED]: "danger",
};

const summaryStatusOptions = Object.values(SupportSummaryStatus).map(
  (value) => ({
    value,
    label: formatEnumLabel(value),
  }),
);

const activeFilterCount = computed(() => {
  let n = 0;
  if (filters.type) n++;
  if (filters.severity) n++;
  if (filters.dueFilter) n++;
  if (filters.summaryStatus) n++;
  if (filters.daysOpen !== null) n++;
  if (filters.daysSinceActivity !== null) n++;
  if (isSupportUser.value && filters.devStatus) n++;
  if (isHavnSupport.value && filters.system) n++;
  return n;
});

const hasActiveFilters = computed(
  () =>
    filters.scope !== "Mine" ||
    filters.ownershipInclude !== "CreatedOrAssigned" ||
    activeFilterCount.value > 0,
);

const quickFilterLabel = computed(() => filters.scope);

const includeLabel = computed(
  () =>
    ownershipIncludeOptions.value.find(
      (o) => o.value === filters.ownershipInclude,
    )?.label ?? filters.ownershipInclude,
);

function formatEnumLabel(value?: string | null) {
  if (!value) return "—";
  return value.replace(/([a-z0-9])([A-Z])/g, "$1 $2");
}

const getTicketTypeLabel = (type: SupportTicketType) =>
  typeOptions.find((option) => option.value === type)?.label ??
  formatEnumLabel(type);

const getAssignmentGroupLabel = (group: SupportAssignmentGroup) =>
  formatEnumLabel(group);

const getSeverityOptionsForType = (
  type: SupportTicketType,
): SeverityOptionDto[] =>
  severityOptions.value.find((option) => option.type === type)?.options ?? [];

const getScopeForRequest = (mode: Exclude<SearchMode, null>): TicketScope => {
  // "All" scope is only meaningful for support users; the backend clamps it for
  // everyone else, but we avoid requesting cross-user/org data from the client too.
  if (isSupportUser.value && (mode === "all" || filters.scope === "All")) {
    return TicketScope.ALL;
  }
  if (filters.scope === "MyOrg") return TicketScope.MY_ORG;
  return filters.ownershipInclude === "Created"
    ? TicketScope.MINE
    : TicketScope.CREATED_OR_ASSIGNED;
};

const buildSupportTicketsSearchRequest = (
  mode: Exclude<SearchMode, null>,
): SearchTicketsRequestDto => {
  const ignoreFilters = mode === "all";

  // Each day-count filter maps onto whichever bound the comparator selected; the other stays
  // undefined so the server treats it as unset.
  const daysOpen = ignoreFilters ? null : filters.daysOpen;
  const daysActivity = ignoreFilters ? null : filters.daysSinceActivity;

  return {
    searchText: searchQuery.value || undefined,
    scope: getScopeForRequest(mode),
    type: ignoreFilters || !filters.type ? undefined : filters.type,
    // Gated on type, not just on its own value: severity numbers are type-scoped, so a
    // severity without a type is meaningless. Clearing Type triggers a search BEFORE the
    // watcher that resets severity runs (watcher registration order), so without this gate
    // that transient request would carry a stale severity.
    severity: ignoreFilters || !filters.type ? undefined : filters.severity,
    summaryStatus:
      ignoreFilters || !filters.summaryStatus
        ? undefined
        : filters.summaryStatus,
    devStatus:
      ignoreFilters || !isSupportUser.value || !filters.devStatus
        ? undefined
        : filters.devStatus,
    dueDate:
      ignoreFilters || !filters.dueFilter
        ? DueDateBucket.ALL
        : filters.dueFilter,
    daysOpenLessThan:
      daysOpen !== null && filters.daysOpenComparator === "lt"
        ? daysOpen
        : undefined,
    daysOpenMoreThan:
      daysOpen !== null && filters.daysOpenComparator === "gt"
        ? daysOpen
        : undefined,
    daysSinceActivityLessThan:
      daysActivity !== null && filters.activityComparator === "lt"
        ? daysActivity
        : undefined,
    daysSinceActivityMoreThan:
      daysActivity !== null && filters.activityComparator === "gt"
        ? daysActivity
        : undefined,
    searchSystems:
      ignoreFilters || !isHavnSupport.value || !filters.system
        ? undefined
        : filters.system,
    page: pagination.page,
    pageSize: pagination.pageSize,
  };
};

const loadTickets = async (mode: Exclude<SearchMode, null> = "filtered") => {
  loading.value = true;
  hasSearched.value = true;
  lastSearchMode.value = mode;
  try {
    const result = await apiClient.tickets.postApiSupportTicketsSearch(
      buildSupportTicketsSearchRequest(mode),
    );

    tickets.value = result.items ?? [];
    total.value = result.totalCount ?? tickets.value.length;
    searchWarnings.value = result.systemWarnings ?? [];
  } catch (err) {
    showError("Error loading support tickets:", err);
  } finally {
    loading.value = false;
  }
};

const runSearchAll = () => {
  pagination.page = 1;
  loadTickets("all");
};

const runSearchWithFilters = () => {
  pagination.page = 1;
  loadTickets("filtered");
};

const filteredTickets = computed(() => {
  return tickets.value;
});

const pagedTickets = computed(() => {
  return filteredTickets.value;
});

const applyFilterChange = () => {
  if (suppressOwnershipWatchers) return;
  pagination.page = 1;
  if (lastSearchMode.value === "all") {
    lastSearchMode.value = "filtered";
  }
  if (hasSearched.value) void loadTickets("filtered");
};

watch(
  () => [
    filters.type,
    filters.severity,
    filters.dueFilter,
    filters.summaryStatus,
    filters.devStatus,
    filters.system,
  ],
  applyFilterChange,
);

// The day-count boxes are typed into, so searching on every keystroke would fire a request
// per digit ("15" would search for 1 then 15). Debounced; the comparators are not.
let daysFilterTimer: ReturnType<typeof setTimeout> | undefined;

watch(
  () => [filters.daysOpen, filters.daysSinceActivity],
  () => {
    if (suppressOwnershipWatchers) return;
    clearTimeout(daysFilterTimer);
    daysFilterTimer = setTimeout(applyFilterChange, 400);
  },
);

watch(
  () => [filters.daysOpenComparator, filters.activityComparator],
  () => {
    // Flipping < / > only changes the result when a count has been entered.
    if (filters.daysOpen === null && filters.daysSinceActivity === null) return;
    applyFilterChange();
  },
);

onBeforeUnmount(() => clearTimeout(daysFilterTimer));

// Ownership controls participate in backend querying; suppress programmatic
// bulk resets so we don't double-search.
let suppressOwnershipWatchers = false;

watch(
  () => filters.scope,
  () => {
    if (suppressOwnershipWatchers) return;
    if (
      !ownershipIncludeOptions.value.some(
        (option) => option.value === filters.ownershipInclude,
      )
    ) {
      filters.ownershipInclude = ownershipIncludeOptions.value[0].value;
      return;
    }
    pagination.page = 1;
    loadTickets("filtered");
  },
);

watch(
  () => filters.ownershipInclude,
  () => {
    if (suppressOwnershipWatchers) return;
    pagination.page = 1;
    loadTickets("filtered");
  },
);

watch(
  () => pagination.pageSize,
  () => {
    if (pagination.page !== 1) {
      pagination.page = 1;
      return;
    }

    if (hasSearched.value) void loadTickets(lastSearchMode.value ?? "filtered");
  },
);

watch(
  () => pagination.page,
  () => {
    if (hasSearched.value) void loadTickets(lastSearchMode.value ?? "filtered");
  },
);

watch(
  () => filters.type,
  (type) => {
    if (!type) {
      filters.severity = null;
      return;
    }

    const validSeverity = getSeverityOptionsForType(type).some(
      (option) => option.value === filters.severity,
    );
    if (!validSeverity) filters.severity = null;
  },
);

onMounted(async () => {
  try {
    [severityOptions.value, ticketSystems.value] = await Promise.all([
      apiClient.tickets.getApiSupportTicketsSeverityOptions(),
      apiClient.tickets.getApiSupportTicketsSystems(),
    ]);
  } catch (err) {
    showError("Error loading support ticket metadata:", err);
  }

  // Auto-search on load with an empty term and the default "Mine" scope.
  await loadTickets("filtered");

  // Arrived via a reminder deep link — open that ticket over the freshly loaded list.
  openTicketFromQuery(route.query.ticket);
});

/**
 * "New Issue" — creates the backing workflow and its draft ticket row server-side, then opens
 * the workflow form. The row has to exist first because the form's fields persist onto it via
 * SaveToTable. Abandoning the form is safe: deleting the workflow cascade-deletes the draft.
 */
const startNewTicket = async () => {
  createTicketLoading.value = true;
  try {
    const started = await apiClient.tickets.postApiSupportTicketsStart();

    trackActivity("Support ticket started", {
      entityType: "SupportTicket",
      entityGuid: started.ticket.guid,
      metadata: { ticketNumber: started.ticket.ticketNumber },
    });

    selectedTicket.value = started.ticket;
    await openWorkflow(started.workflowGuid);
  } catch (error) {
    showError(error);
  } finally {
    createTicketLoading.value = false;
  }
};

/**
 * Opens a ticket by opening the workflow behind it — the workflow form is the ticket form.
 *
 * A ticket with no workflowGuid predates the workflow form (or its workflow creation failed),
 * so there is nothing to open. Say so rather than appearing to ignore the click.
 */
const openTicket = async (row: TicketDto) => {
  // A cross-system row's workflow lives on another environment — the local workflow API
  // would 404 on its guid. It opens read-only through the remote-workflow endpoint instead.
  if (row.isRemote) {
    openRemoteTicket(row);
    return;
  }
  await openTicketByGuid(row.guid);
};

/**
 * Fetches the open (remote) ticket's workflow content from its own system; null when the
 * drawer shows a local ticket. Handed to the viewer as its generic contentLoader — which
 * forces read-only — so all ticket/system knowledge stays on this page.
 */
const remoteContentLoader = ref<(() => Promise<WorkflowContentDto>) | null>(
  null,
);

const openRemoteTicket = (row: TicketDto) => {
  // sourceSystem on a remote row IS the config key — unique per system, unlike site types.
  const system = ticketSystems.value?.remoteSystems.find(
    (s) => s.key === row.sourceSystem,
  );
  if (!system) {
    ElMessage.warning(
      `The system this ticket lives on (${row.sourceSystem}) is not configured for cross-system access.`,
    );
    return;
  }
  if (!row.workflowGuid) {
    ElMessage.warning(
      `Ticket #${row.ticketNumber} has no workflow attached, so it cannot be opened.`,
    );
    return;
  }

  selectedTicket.value = row;
  remoteContentLoader.value = () =>
    apiClient.tickets.getApiSupportTicketsRemoteWorkflow(system.key, row.guid);
  selectedWorkflow.value = {
    guid: row.workflowGuid,
    workflowStatus: "",
    workflowName: `Ticket #${row.ticketNumber} — ${system.key} (read-only)`,
    workflowArchivedAt: "",
    workflowDefinitionName: "Support Ticket",
    workflowDefinitionGuid: "",
    ownerName: row.primaryContact ?? "",
    ownerGuid: undefined,
    answersToShow: {},
    updatedAt: row.updatedAt ?? "",
  } as WorkflowDto;
};

const openTicketByGuid = async (ticketGuid: string) => {
  detailLoading.value = true;
  try {
    const ticket = await apiClient.tickets.getTicket(ticketGuid);
    selectedTicket.value = ticket;

    if (!ticket.workflowGuid) {
      ElMessage.warning(
        `Ticket #${ticket.ticketNumber} has no workflow attached, so it cannot be opened.`,
      );
      selectedTicket.value = null;
      return;
    }

    await openWorkflow(ticket.workflowGuid);
  } catch (error) {
    showError(error);
  } finally {
    detailLoading.value = false;
  }
};

/**
 * Deep link from a support reminder: `?ticket=<guid>` opens that ticket's workflow viewer on
 * top of the list. Watched (not just read on mount) so clicking a reminder while already on
 * this page still opens the drawer.
 */
const openTicketFromQuery = (param: unknown) => {
  if (typeof param === "string" && param.length > 0) {
    void openTicketByGuid(param);
  }
};

watch(() => route.query.ticket, openTicketFromQuery);

/** Loads a workflow and shows it in the drawer. */
const openWorkflow = async (workflowGuid: string) => {
  const content = await apiClient.workflowInstance.getWorkflow(workflowGuid);

  selectedWorkflow.value = {
    guid: content.workflow.guid,
    workflowStatus: content.workflow.status ?? "",
    workflowName: content.workflow.name ?? "",
    workflowArchivedAt: content.workflow.archivedAt ?? "",
    workflowDefinitionName: content.workflow.workflowDefinition?.name ?? "",
    workflowDefinitionGuid: content.workflow.workflowDefinition?.guid ?? "",
    ownerName: selectedTicket.value?.primaryContact ?? "",
    ownerGuid: undefined,
    answersToShow: {},
    updatedAt: content.workflow.createdAt ?? "",
  } as WorkflowDto;
};

/**
 * Closing the workflow refreshes the list — the form writes ticket columns through
 * SaveToTable, so subject/type/severity may all have changed while it was open.
 */
const closeWorkflowDrawer = async () => {
  selectedWorkflow.value = null;
  selectedTicket.value = null;
  remoteContentLoader.value = null;

  // Drop a reminder deep link once its drawer closes — otherwise the same reminder can't be
  // clicked again (same query → no route change → the watcher never fires).
  if (route.query.ticket) {
    const { ticket: _ticket, ...rest } = route.query;
    void router.replace({ query: rest });
  }

  await loadTickets(lastSearchMode.value ?? "filtered");
};

/**
 * Tooltip for the Days Open cell — the actual deadline behind the badge, so a user can see
 * *when* something is due rather than only that it is late.
 */
const getDueTooltip = (ticket: TicketDto): string => {
  if (!ticket.resolutionDueAt)
    return "No SLA applies until type and severity are set";
  const due = new Date(ticket.resolutionDueAt).toLocaleString();
  return ticket.dueBucket === DueDateBucket.OVERDUE
    ? `Was due ${due}`
    : `Due ${due}`;
};

const getStatusTagType = (
  status?: SupportDevelopmentStatus,
): "info" | "primary" | "warning" | "success" | "danger" => {
  switch (status) {
    case SupportDevelopmentStatus.COMPLETED:
      return "success";
    case SupportDevelopmentStatus.CANCELLED:
      return "danger";
    case SupportDevelopmentStatus.IN_DEVELOPMENT:
    case SupportDevelopmentStatus.IN_TEST:
    case SupportDevelopmentStatus.ON_UAT:
      return "warning";
    case SupportDevelopmentStatus.NEW:
      return "primary";
    default:
      return "info";
  }
};

const resetFilters = () => {
  suppressOwnershipWatchers = true;
  filters.scope = "Mine";
  filters.ownershipInclude = "CreatedOrAssigned";
  filters.type = "";
  filters.severity = null;
  filters.dueFilter = "";
  filters.summaryStatus = "";
  filters.devStatus = "";
  filters.daysOpenComparator = "gt";
  filters.daysOpen = null;
  filters.activityComparator = "gt";
  filters.daysSinceActivity = null;
  filters.system = "";
  pagination.page = 1;
  searchQuery.value = "";
  void nextTick(() => {
    suppressOwnershipWatchers = false;
  });

  if (hasSearched.value) runSearchWithFilters();
};
</script>

<template>
  <div class="support-tickets-page">
    <div class="page-header">
      <h2>Support Tickets</h2>
    </div>

    <div class="search-bar support-tickets">
      <div class="search-bar__main">
        <div class="search-bar__zone search-bar__zone--search">
          <span class="search-bar__zone-label">Search</span>

          <el-button
            type="primary"
            :icon="Plus"
            :loading="createTicketLoading"
            size="small"
            @click="startNewTicket"
          >
            New Issue
          </el-button>

          <el-input
            v-model="searchQuery"
            :prefix-icon="Search"
            placeholder="Search subject, created date, contact, ticket #, GitHub #..."
            class="search-bar__input"
            clearable
            @keyup.enter="runSearchAll"
          />

          <el-button
            type="primary"
            :icon="Search"
            size="small"
            :loading="loading && lastSearchMode === 'all'"
            @click="runSearchAll"
          >
            Search All
          </el-button>

          <el-button
            type="primary"
            plain
            :icon="Search"
            size="small"
            :loading="loading && lastSearchMode === 'filtered'"
            @click="runSearchWithFilters"
          >
            Search with Filters
          </el-button>
        </div>

        <div class="search-bar__zone search-bar__zone--filters">
          <span class="search-bar__zone-label">Filters</span>

          <div class="filter-group-inline filter-group-shaded">
            <el-radio-group
              v-model="filters.scope"
              size="small"
              class="search-bar__quick-filter"
            >
              <el-radio-button value="Mine">Mine</el-radio-button>
              <el-radio-button value="MyOrg">My Org</el-radio-button>
              <el-radio-button v-if="isSupportUser" value="All"
                >All</el-radio-button
              >
            </el-radio-group>

            <el-select
              v-model="filters.ownershipInclude"
              class="filter-select"
              placeholder="Created / Assigned"
              size="small"
            >
              <el-option
                v-for="opt in ownershipIncludeOptions"
                :key="opt.value"
                :label="opt.label"
                :value="opt.value"
              />
            </el-select>
          </div>

          <div class="search-bar__divider" style="margin: 0 4px" />

          <el-button
            size="small"
            plain
            :disabled="!searchQuery && !hasActiveFilters"
            @click="resetFilters"
          >
            Reset Filters
          </el-button>
        </div>
      </div>

      <div class="search-bar__filters">
        <div class="filter-group-shaded filter-group-shaded--panel">
          <div class="filter-group">
            <span class="filter-label">Ticket</span>
            <div class="filter-row-inline">
              <el-select
                v-model="filters.type"
                placeholder="Type"
                clearable
                class="filter-select"
                size="small"
              >
                <el-option
                  v-for="t in typeOptions"
                  :key="t.value"
                  :label="t.label"
                  :value="t.value"
                />
              </el-select>

              <!--
                Severity scales differ per ticket type (Bug 1-5, Outage 1-6...), so the
                options only exist once a Type is chosen. Disabled until then — an enabled
                dropdown with "No data" reads as broken.
              -->
              <el-tooltip
                content="Select a Type first"
                placement="top"
                :disabled="!!filters.type"
                :show-after="300"
              >
                <el-select
                  v-model="filters.severity"
                  placeholder="Severity"
                  clearable
                  :disabled="!filters.type"
                  class="filter-select"
                  size="small"
                >
                  <el-option
                    v-for="s in filters.type
                      ? getSeverityOptionsForType(filters.type)
                      : []"
                    :key="s.value"
                    :label="s.label"
                    :value="s.value"
                  />
                </el-select>
              </el-tooltip>

              <!--
                These are SLA buckets, so "On track" (the server calls it Open) means
                "has a deadline and is meeting it" — it deliberately excludes tickets with
                no SLA at all (no type/severity yet). The label matches the row badge.
                Cleared = no narrowing (server gets All), the same idiom as its siblings —
                so there is no explicit "All" option.
              -->
              <el-select
                v-model="filters.dueFilter"
                placeholder="Due Date"
                clearable
                class="filter-select"
                size="small"
              >
                <el-option label="On track" :value="DueDateBucket.OPEN" />
                <el-option label="Due soon" :value="DueDateBucket.DUE_SOON" />
                <el-option label="Overdue" :value="DueDateBucket.OVERDUE" />
              </el-select>

              <el-select
                v-model="filters.summaryStatus"
                placeholder="Status"
                clearable
                class="filter-select"
                size="small"
              >
                <el-option
                  v-for="s in summaryStatusOptions"
                  :key="s.value"
                  :label="s.label"
                  :value="s.value"
                />
              </el-select>
            </div>
          </div>

          <div class="filter-panel-divider" />

          <!-- Spec: "Days Open or Last Activity — < x days / > x days" -->
          <div class="filter-group">
            <span class="filter-label">Age</span>
            <div class="filter-row-inline">
              <div class="activity-filter">
                <span class="filter-sublabel">Open</span>
                <el-select
                  v-model="filters.daysOpenComparator"
                  class="comparator-select"
                  size="small"
                >
                  <el-option label="&lt;" value="lt" />
                  <el-option label="&gt;" value="gt" />
                </el-select>
                <el-input-number
                  v-model="filters.daysOpen"
                  :min="0"
                  :controls="false"
                  placeholder="days"
                  class="days-input"
                  size="small"
                />
              </div>

              <div class="activity-filter">
                <span class="filter-sublabel">Last activity</span>
                <el-select
                  v-model="filters.activityComparator"
                  class="comparator-select"
                  size="small"
                >
                  <el-option label="&lt;" value="lt" />
                  <el-option label="&gt;" value="gt" />
                </el-select>
                <el-input-number
                  v-model="filters.daysSinceActivity"
                  :min="0"
                  :controls="false"
                  placeholder="days"
                  class="days-input"
                  size="small"
                />
              </div>
            </div>
          </div>

          <template v-if="isSupportUser">
            <div class="filter-panel-divider" />

            <div class="filter-group">
              <span class="filter-label">Support</span>
              <div class="filter-row-inline">
                <el-select
                  v-model="filters.devStatus"
                  placeholder="Dev Status"
                  clearable
                  class="filter-select"
                  size="small"
                >
                  <el-option
                    v-for="d in devStatusOptions"
                    :key="d.value"
                    :label="d.label"
                    :value="d.value"
                  />
                </el-select>
              </div>
            </div>
          </template>

          <!--
            Which system(s) to search — options come from the server (systems metadata), and
            only HAVN Support gets them (the server's flag, so SuperAdmin counts too). Hidden
            entirely when no sibling systems are configured: a dropdown with one option is
            noise. Gated separately from the Support group above because Client Support may
            see that group but must not see this.
          -->
          <template v-if="isHavnSupport && hasRemoteSystems">
            <div class="filter-panel-divider" />

            <div class="filter-group">
              <span class="filter-label">System</span>
              <div class="filter-row-inline">
                <el-select
                  v-model="filters.system"
                  class="filter-select"
                  size="small"
                >
                  <el-option
                    v-for="opt in systemFilterOptions"
                    :key="opt.value"
                    :label="opt.label"
                    :value="opt.value"
                  />
                </el-select>
              </div>
            </div>
          </template>
        </div>
      </div>

      <div v-if="hasSearched" class="search-bar__results">
        <span class="results-count">
          Showing <strong>{{ tickets.length }}</strong> of
          <strong>{{ total }}</strong>
        </span>

        <span class="results-mode" :class="`results-mode--${lastSearchMode}`">
          {{ lastSearchMode === "all" ? "All Results" : "Filters Applied" }}
        </span>
        <span
          v-if="lastSearchMode === 'all' && hasActiveFilters"
          class="filters-ignored-notice"
        >
          Filters ignored.
          <button
            type="button"
            class="filters-ignored-notice__action"
            @click="runSearchWithFilters"
          >
            Apply them
          </button>
        </span>

        <span
          class="active-chip active-chip--readonly"
          :class="{ 'active-chip--ignored': lastSearchMode === 'all' }"
        >
          {{ quickFilterLabel }}
        </span>

        <span
          class="active-chip active-chip--readonly"
          :class="{ 'active-chip--ignored': lastSearchMode === 'all' }"
        >
          {{ includeLabel }}
        </span>

        <!-- A sibling system being down degrades to a partial result — say so. -->
        <span v-if="searchWarnings.length" class="system-warnings">
          {{ searchWarnings.join(" ") }} Results are incomplete.
        </span>
      </div>
    </div>

    <el-table
      v-loading="loading"
      :data="pagedTickets"
      class="tickets-table"
      @row-click="openTicket"
    >
      <el-table-column prop="ticketNumber" label="Ticket #" width="120" />
      <el-table-column
        prop="subject"
        label="Subject"
        min-width="100"
        show-overflow-tooltip
      />
      <el-table-column label="Type" width="150">
        <template #default="{ row }">
          {{ getTicketTypeLabel(row.type) }}
        </template>
      </el-table-column>
      <el-table-column label="Severity" width="300">
        <template #default="{ row }">
          <!-- No empty tag for a ticket without a severity (draft, or a value that
               failed to save) — a blank chip reads as a rendering bug. -->
          <el-tag
            v-if="row.severityLabel != null || row.severity != null"
            type="info"
            size="small"
          >
            {{ row.severityLabel ?? row.severity }}
          </el-tag>
          <span v-else class="muted">—</span>
        </template>
      </el-table-column>
      <!-- <el-table-column
        prop="primaryContact"
        label="Primary Contact"
        width="180"
        show-overflow-tooltip
      /> -->
      <el-table-column prop="createdByName" label="Created By" width="170">
        <template #default="{ row }">
          <span v-if="row.createdByName">{{ row.createdByName }}</span>
          <span v-else class="muted">—</span>
        </template>
      </el-table-column>
      <!--
        Days open plus the SLA badge. Both the count and the bucket come from the server —
        the bucket reflects this ticket's own severity and the business calendar, so it always
        agrees with the Due Date filter.
      -->
      <el-table-column label="Days Open" width="150">
        <template #default="{ row }">
          <el-tooltip
            :content="getDueTooltip(row)"
            placement="top"
            :show-after="300"
          >
            <span class="days-open-cell">
              {{ row.daysOpen }}
              <el-tag
                v-if="row.dueBucket && row.dueBucket !== DueDateBucket.OPEN"
                :type="dueBucketTagType[row.dueBucket as DueDateBucket]"
                size="small"
                effect="plain"
              >
                {{ dueBucketLabel[row.dueBucket as DueDateBucket] }}
              </el-tag>
            </span>
          </el-tooltip>
        </template>
      </el-table-column>
      <!--
        Status. Everyone sees the summary; support users additionally get the underlying
        development status in its own column below. "(days in status)" per the spec.
      -->
      <el-table-column label="Status" width="150">
        <template #default="{ row }">
          <el-tag
            :type="
              summaryStatusTagType[row.summaryStatus as SupportSummaryStatus]
            "
            size="small"
          >
            {{ formatEnumLabel(row.summaryStatus) }}
          </el-tag>
          <div class="status-days">{{ row.daysInStatus }}d in status</div>
        </template>
      </el-table-column>
      <el-table-column label="Assignment" width="150">
        <template #default="{ row }">
          {{ getAssignmentGroupLabel(row.assignedGroup) }}
        </template>
      </el-table-column>
      <el-table-column prop="updatedAt" label="Last Updated" width="140">
        <template #default="{ row }">
          {{ new Date(row.updatedAt).toLocaleDateString() }}
        </template>
      </el-table-column>
      <el-table-column
        v-if="isSupportUser"
        prop="gitHubNumber"
        label="GitHub #"
        width="110"
      >
        <template #default="{ row }">
          <span v-if="row.gitHubNumber">#{{ row.gitHubNumber }}</span>
          <span v-else class="muted">—</span>
        </template>
      </el-table-column>
      <el-table-column
        v-if="isSupportUser"
        prop="developmentStatus"
        label="Dev Status"
        width="130"
      >
        <template #default="{ row }">
          <el-tag
            v-if="row.developmentStatus"
            :type="getStatusTagType(row.developmentStatus)"
            size="small"
          >
            {{ formatEnumLabel(row.developmentStatus) }}
          </el-tag>
          <span v-else class="muted">—</span>
        </template>
      </el-table-column>
      <!--
        Which system the row lives on (cross-system searches) or was reported from. Remote
        rows are read-only — their workflow exists only on that environment — so they get an
        explicit marker rather than silently behaving differently on click.
      -->
      <el-table-column v-if="isSupportUser" label="System" width="250">
        <template #default="{ row }">
          <span
            v-if="row.sourceSystem || row.systemReportedFrom"
            class="system-cell"
          >
            <!-- Remote rows carry their system's config key verbatim; only real site types
                 go through the display-name mapping. -->
            {{
              row.isRemote
                ? row.sourceSystem
                : displaySiteType(row.sourceSystem ?? row.systemReportedFrom)
            }}
            <el-tooltip
              v-if="row.isRemote"
              content="Lives on another system — opens read-only"
              placement="top"
              :show-after="300"
            >
              <el-tag size="small" type="warning" effect="plain">remote</el-tag>
            </el-tooltip>
          </span>
          <span v-else class="muted">—</span>
        </template>
      </el-table-column>
    </el-table>

    <div class="pagination-bar">
      <el-pagination
        v-model:current-page="pagination.page"
        v-model:page-size="pagination.pageSize"
        :page-sizes="[25, 50, 100]"
        layout="total, sizes, prev, pager, next"
        :total="total"
      />
    </div>

    <!--
      The ticket form itself. Its fields write back onto the ticket row via SaveToTable, so
      this is both the "new ticket" form and the way an existing ticket is worked on.
    -->
    <!-- <el-drawer
      size="70%"
      :with-header="false"
      destroy-on-close
      class="support-workflow-drawer"
    > -->
    <WorkflowResponseViewer
      v-if="selectedWorkflow"
      v-model="workflowDrawerVisible"
      :compliance-workflow="selectedWorkflow"
      :full-screen="false"
      :content-loader="remoteContentLoader ?? undefined"
      @close-drawer="closeWorkflowDrawer"
      @refresh-list="closeWorkflowDrawer"
    />
    <!-- </el-drawer> -->
  </div>
</template>

<style lang="less" scoped>
.support-tickets-page {
  .page-header {
    display: flex;
    align-items: center;
    margin-bottom: 1em;

    h2 {
      margin: 0;
      color: var(--el-color-primary);
    }
  }

  .search-bar {
    background: var(--el-bg-color, #fff);
    margin-bottom: 1em;
  }

  .search-bar__main {
    display: flex;
    align-items: stretch;
    gap: 0;
    flex-wrap: wrap;
  }

  .search-bar__zone {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
    padding: 7px 12px;
    position: relative;
    align-self: stretch;
  }

  .search-bar__zone--search {
    background: var(--el-color-primary-light-9, #ecf5ff);
    border-bottom: 2px solid var(--el-color-primary-light-7, #c6e2ff);
    border-right: 1px solid var(--el-color-primary-light-7, #c6e2ff);
    flex: 0 0 auto;
  }

  .search-bar__zone--filters {
    background: var(--el-fill-color, #f0f2f5);
    border-bottom: 2px solid var(--el-border-color, #dcdfe6);
    flex: 1 1 auto;
    min-width: 0;
  }

  @media (max-width: 1100px) {
    .search-bar__zone--search {
      flex: 0 0 100%;
      border-right: none;
      border-bottom: 1px solid var(--el-color-primary-light-7, #c6e2ff);
    }

    .search-bar__zone--filters {
      flex: 0 0 100%;
    }
  }

  .search-bar__zone-label {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    white-space: nowrap;
    flex-shrink: 0;
    padding-right: 8px;
    margin-right: 2px;
    align-self: center;
    height: 16px;
    line-height: 16px;
    color: var(--el-text-color-regular, #606266);
  }

  .search-bar__zone--search .search-bar__zone-label {
    border-right: 1px solid var(--el-color-primary-light-6, #a0cfff);
  }

  .search-bar__zone--filters .search-bar__zone-label {
    border-right: 1px solid var(--el-border-color, #dcdfe6);
  }

  .search-bar__input {
    width: 470px;
  }

  .search-bar__divider {
    width: 1px;
    height: 22px;
    background: var(--el-border-color-lighter, #e4e7ed);
    flex-shrink: 0;
    margin: 0 2px;
  }

  .search-bar__quick-filter {
    flex-shrink: 0;
  }

  .search-bar__filters {
    border-top: 1px solid var(--el-border-color-lighter, #e4e7ed);
    padding: 10px 12px;
    display: flex;
    align-items: flex-end;
    gap: 10px;
    flex-wrap: wrap;
    background: var(--el-fill-color-lighter, #fafafa);
  }

  .search-bar__results {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-top: 1px solid var(--el-border-color-lighter, #e4e7ed);
    background: var(--el-fill-color-lighter, #fafafa);
    min-height: 36px;
    flex-wrap: wrap;
  }

  .filter-group-inline {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .filter-row-inline {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .filter-group {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex-wrap: wrap;
  }

  .filter-label {
    font-size: 12px;
    font-weight: 600;
    color: var(--el-text-color-secondary);
    white-space: nowrap;
  }

  .filter-panel-divider {
    width: 1px;
    height: 56px;
    background: var(--el-border-color-lighter, #e4e7ed);
    align-self: flex-end;
    flex-shrink: 0;
    margin: 0 4px;
  }

  .filter-group-shaded--panel {
    display: flex;
    align-items: flex-end;
    gap: 12px;
    flex-wrap: wrap;
    padding: 8px 12px;

    .filter-select {
      width: 160px;
    }
  }

  .filter-select {
    width: 160px;
  }

  .filter-select-sm {
    width: 140px;
  }

  .comparator-select {
    width: 70px;
  }

  .days-input {
    width: 90px;
  }

  .activity-filter {
    display: flex;
    gap: 0.4em;
    align-items: center;
  }

  .filter-group-shaded {
    background: var(--el-fill-color-light, #f5f7fa);
    border: 1px solid var(--el-border-color-lighter, #e4e7ed);
    border-radius: 6px;
    padding: 4px 8px;
    display: flex;
    align-items: center;
    gap: 6px;

    &__label {
      font-size: 11px;
      font-weight: 600;
      color: var(--el-text-color-secondary);
      text-transform: uppercase;
      letter-spacing: 0.04em;
      white-space: nowrap;
      margin-right: 2px;
    }

    &--activity {
      background: var(--el-fill-color, #eef1f4);
      border-color: var(--el-border-color, #dcdfe6);
    }
  }

  .results-count {
    font-size: 12px;
    color: var(--el-text-color-secondary);

    strong {
      color: var(--el-text-color-primary);
      font-weight: 600;
    }
  }

  .results-mode {
    display: inline-flex;
    align-items: center;
    height: 20px;
    padding: 0 8px;
    border-radius: 100px;
    font-size: 11px;
    font-weight: 600;
    border: 1px solid transparent;
  }

  .results-mode--filtered {
    background: var(--el-color-primary-light-9);
    color: var(--el-color-primary);
    border-color: var(--el-color-primary-light-5);
  }

  .results-mode--all {
    background: var(--el-fill-color);
    color: var(--el-text-color-regular);
    border-color: var(--el-border-color-lighter);
  }

  .active-chip {
    display: inline-flex;
    align-items: center;
    height: 22px;
    padding: 0 8px;
    border-radius: 100px;
    font-size: 11px;
    border: 1px solid var(--el-color-primary-light-5);
    background: var(--el-color-primary-light-9);
    color: var(--el-color-primary);

    &--readonly {
      background: var(--el-fill-color-light, #f5f7fa);
      color: var(--el-text-color-secondary);
      border-color: var(--el-border-color-lighter, #e4e7ed);
    }

    &--ignored {
      background: var(--el-fill-color);
      color: var(--el-text-color-placeholder);
      border-color: var(--el-border-color-lighter);
      border-style: dashed;
      text-decoration: line-through;
      text-decoration-color: var(--el-text-color-placeholder);
    }
  }

  .filters-ignored-notice {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    color: var(--el-color-warning);
    font-style: italic;
  }

  .system-warnings {
    display: inline-flex;
    align-items: center;
    font-size: 11px;
    color: var(--el-color-danger);
    font-style: italic;
  }

  .system-cell {
    display: inline-flex;
    align-items: center;
    gap: 0.4em;
  }

  .filters-ignored-notice__action {
    background: none;
    border: none;
    padding: 0;
    color: var(--el-color-primary);
    text-decoration: underline;
    cursor: pointer;
    font-size: 11px;
    font-family: inherit;
    font-weight: 600;

    &:hover {
      color: var(--el-color-primary-light-3);
    }
  }

  .tickets-table {
    width: 100%;
    :deep(.el-table__row) {
      cursor: pointer;
    }
  }

  .status-days {
    font-size: 0.75rem;
    color: var(--el-text-color-secondary);
    margin-top: 0.2em;
  }

  .days-open-cell {
    display: inline-flex;
    align-items: center;
    gap: 0.4em;
  }

  .filter-sublabel {
    font-size: 11px;
    color: var(--el-text-color-secondary);
    white-space: nowrap;
  }

  .muted {
    color: var(--el-text-color-placeholder);
  }

  .pagination-bar {
    display: flex;
    justify-content: flex-end;
    margin-top: 1em;
  }

  .w-100 {
    width: 100%;
  }
}
</style>
